import React, { useEffect, useMemo, useState } from "react";
import {
  useGetCustomAgentFileQuery,
  usePutCustomAgentFileMutation,
} from "../../../../app/api/console/customAgentsApiSlice";
import FrontmatterEditor from "./FrontmatterEditor";
import MarkdownField from "./MarkdownField";

export function FileEditor({ env, slug, fileKey, onSaved }) {
  // Use currentData (result for THIS fileKey), not data (last success for any
  // args). RTK keeps `data` pointing at the previous file while the next key
  // is fetching — that stale buffer + Save would overwrite the newly selected
  // S3 object with the prior file's contents.
  const { currentData, isFetching, isError, error: queryError, refetch } =
    useGetCustomAgentFileQuery({ env, slug, key: fileKey }, { skip: !fileKey });
  const [putFile, { isLoading: isSaving }] = usePutCustomAgentFileMutation();
  const [mode, setMode] = useState("frontmatter"); // frontmatter | body | raw
  const [rawText, setRawText] = useState("");
  const [bodyText, setBodyText] = useState("");
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);

  const fileReady = Boolean(currentData && currentData.key === fileKey);

  // Drop buffers immediately on key change so a remount-less switch cannot
  // briefly expose (or save) the previous file's text under the new key.
  useEffect(() => {
    setRawText("");
    setBodyText("");
    setDirty(false);
    setError(null);
  }, [fileKey]);

  useEffect(() => {
    if (!fileReady) return;
    setRawText(currentData.text || "");
    setBodyText(currentData.body || "");
    setDirty(false);
    setError(null);
  }, [fileReady, currentData]);

  const handleSaveRawOrBody = async () => {
    if (!fileReady) {
      setError("Wait for this file to finish loading before saving.");
      return;
    }
    setError(null);
    let contents = rawText;
    if (mode === "body") {
      const fmMatch = (currentData?.text || "").match(/^---\s*\n[\s\S]*?\n---\s*\n?/);
      if (fmMatch) {
        contents = fmMatch[0] + (bodyText.startsWith("\n") ? bodyText.slice(1) : bodyText);
        if (!contents.endsWith("\n")) contents += "\n";
      } else {
        contents = bodyText;
      }
    }
    try {
      await putFile({ env, slug, key: fileKey, contents }).unwrap();
      setDirty(false);
      onSaved?.();
      refetch();
    } catch (err) {
      setError(err?.data?.detail || err?.error || "Save failed");
    }
  };

  if (isFetching && !fileReady) {
    return (
      <div
        className="flex items-center justify-center h-24"
        data-testid="file-editor-loading"
      >
        <div className="animate-spin rounded-full h-5 w-5 border-2 border-accent-purple border-t-transparent" />
      </div>
    );
  }

  if (isError || !fileReady) {
    const detail =
      queryError?.data?.detail ||
      queryError?.error ||
      "Could not load this file.";
    return (
      <div
        className="text-body-xs text-red-600 bg-red-50 rounded-lg px-3 py-2"
        data-testid="file-editor-load-error"
      >
        {detail}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="file-editor-ready">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-1 rounded-xl overflow-hidden border border-border-subtle w-fit">
          {[
            { id: "frontmatter", label: "Frontmatter" },
            { id: "body", label: "Body" },
            { id: "raw", label: "Raw file" },
          ].map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setMode(t.id)}
              className={`px-3 py-1.5 text-body-xs font-medium transition-colors ${
                mode === t.id
                  ? "bg-button-primary text-white"
                  : "bg-white text-text-secondary hover:bg-surface-neutral"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {(mode === "body" || mode === "raw") && (
          <button
            type="button"
            onClick={handleSaveRawOrBody}
            disabled={!dirty || isSaving || !fileReady}
            data-testid="file-editor-save"
            className="px-3 py-1.5 bg-button-primary text-white rounded-lg text-body-xs font-semibold disabled:opacity-40"
          >
            {isSaving ? "Saving…" : mode === "body" ? "Save body" : "Save file"}
          </button>
        )}
      </div>

      {error && (
        <div className="text-body-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</div>
      )}

      {mode === "frontmatter" && (
        <FrontmatterEditor
          key={fileKey}
          env={env}
          slug={slug}
          fileKey={fileKey}
          frontmatter={currentData.frontmatter || {}}
          onSaved={() => {
            refetch();
            onSaved?.();
          }}
        />
      )}

      {mode === "body" && (
        <MarkdownField
          label="Skill body"
          value={bodyText}
          onChange={(v) => {
            setBodyText(v);
            setDirty(true);
          }}
          rows={18}
          mono
          minHeightClass="min-h-[360px]"
          hint="Markdown body below the YAML frontmatter. Preview renders GFM-ish markdown."
        />
      )}

      {mode === "raw" && (
        <textarea
          value={rawText}
          onChange={(e) => {
            setRawText(e.target.value);
            setDirty(true);
          }}
          className="w-full min-h-[420px] px-3 py-2.5 border border-border-subtle rounded-xl text-body-xs font-mono focus:outline-none focus:ring-2 focus:ring-accent-purple/20"
        />
      )}
    </div>
  );
}

function buildSkillTree(skills) {
  const websites = [];
  const byWebsite = new Map();

  for (const skill of skills || []) {
    const wid = skill.website_id || "unknown";
    const wname = skill.website_name || wid;
    if (!byWebsite.has(wid)) {
      const node = { website_id: wid, website_name: wname, skills: [] };
      byWebsite.set(wid, node);
      websites.push(node);
    }
    byWebsite.get(wid).skills.push(skill);
  }

  return websites;
}

function SkillsTab({ env, slug, skills, onRefresh }) {
  const tree = useMemo(() => buildSkillTree(skills), [skills]);

  const flatSelectable = useMemo(() => {
    const rows = [];
    for (const skill of skills || []) {
      if (skill.runtime_agent) {
        rows.push({
          id: `runtime:${skill.skill_name}`,
          skill,
          file: null,
          note: skill.note,
        });
        continue;
      }
      for (const file of skill.files || []) {
        rows.push({
          id: file.key,
          skill,
          file,
        });
      }
    }
    return rows;
  }, [skills]);

  const [selectedId, setSelectedId] = useState(null);
  const [collapsedSites, setCollapsedSites] = useState({});
  const [collapsedSkills, setCollapsedSkills] = useState({});

  useEffect(() => {
    if (!selectedId && flatSelectable.length) {
      const firstEditable = flatSelectable.find((r) => r.file);
      setSelectedId(firstEditable?.id || flatSelectable[0].id);
    }
  }, [flatSelectable, selectedId]);

  const selected = flatSelectable.find((r) => r.id === selectedId);

  if (!skills?.length) {
    return (
      <div className="text-body-sm text-text-tertiary py-8 text-center">
        No custom skills found on S3 for this agent&apos;s websites.
        <br />
        Check that <code className="text-xs">launch_prompt</code> references{" "}
        <code className="text-xs">custom/&lt;skill&gt;</code> and the skill is seeded.
      </div>
    );
  }

  return (
    <div className="flex gap-4 min-h-[420px]">
      <div className="w-80 shrink-0 border border-border-subtle rounded-xl overflow-hidden flex flex-col">
        <div className="px-3 py-2 bg-surface-neutral border-b border-border-subtle text-body-xs font-semibold text-text-secondary">
          Skills &amp; subskills
        </div>
        <div className="overflow-y-auto max-h-[560px] flex-1">
          {tree.map((site) => {
            const siteCollapsed = Boolean(collapsedSites[site.website_id]);
            return (
              <div key={site.website_id} className="border-b border-border-subtle">
                <button
                  type="button"
                  onClick={() =>
                    setCollapsedSites((prev) => ({
                      ...prev,
                      [site.website_id]: !prev[site.website_id],
                    }))
                  }
                  className="w-full flex items-center gap-1.5 px-3 py-2 text-left bg-surface-neutral/60 hover:bg-surface-neutral"
                >
                  <span className="text-caption text-text-tertiary w-3">
                    {siteCollapsed ? "▸" : "▾"}
                  </span>
                  <div className="min-w-0">
                    <div className="text-body-xs font-semibold text-text-primary truncate">
                      {site.website_name}
                    </div>
                    <div className="text-[10px] text-text-tertiary font-mono truncate">
                      {site.website_id}
                    </div>
                  </div>
                </button>
                {!siteCollapsed &&
                  site.skills.map((skill) => {
                    const skillKey = `${site.website_id}:${skill.skill_name}`;
                    const skillCollapsed = Boolean(collapsedSkills[skillKey]);
                    if (skill.runtime_agent) {
                      const id = `runtime:${skill.skill_name}`;
                      return (
                        <button
                          key={id}
                          type="button"
                          onClick={() => setSelectedId(id)}
                          className={`w-full text-left pl-7 pr-3 py-2 text-body-xs border-t border-border-subtle ${
                            selectedId === id
                              ? "bg-accent-purple/5 text-text-primary"
                              : "hover:bg-surface-neutral text-text-secondary"
                          }`}
                        >
                          <div className="font-medium truncate">{skill.skill_name}</div>
                          <div className="text-caption text-text-tertiary">Runtime agent</div>
                        </button>
                      );
                    }
                    return (
                      <div key={skillKey}>
                        <button
                          type="button"
                          onClick={() =>
                            setCollapsedSkills((prev) => ({
                              ...prev,
                              [skillKey]: !prev[skillKey],
                            }))
                          }
                          className="w-full flex items-center gap-1.5 pl-5 pr-3 py-1.5 text-left hover:bg-surface-neutral border-t border-border-subtle"
                        >
                          <span className="text-caption text-text-tertiary w-3">
                            {skillCollapsed ? "▸" : "▾"}
                          </span>
                          <span className="text-body-xs font-medium text-text-primary truncate">
                            {skill.skill_name}
                          </span>
                          <span className="ml-auto text-[10px] text-text-tertiary">
                            {(skill.files || []).length}
                          </span>
                        </button>
                        {!skillCollapsed &&
                          (skill.files || []).map((file) => (
                            <button
                              key={file.key}
                              type="button"
                              onClick={() => setSelectedId(file.key)}
                              className={`w-full text-left pl-10 pr-3 py-2 text-body-xs border-t border-border-subtle/70 ${
                                selectedId === file.key
                                  ? "bg-accent-purple/5 text-text-primary"
                                  : "hover:bg-surface-neutral text-text-secondary"
                              }`}
                            >
                              <div className="font-medium truncate">{file.rel}</div>
                              <div className="text-caption text-text-tertiary capitalize">
                                {file.kind || "file"}
                              </div>
                            </button>
                          ))}
                      </div>
                    );
                  })}
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex-1 min-w-0">
        {selected?.file && (
          <div className="mb-3">
            <div className="text-body-xs font-semibold text-text-primary truncate">
              {selected.skill?.skill_name}
              <span className="text-text-tertiary font-normal">
                {" "}
                · {selected.file.rel}
              </span>
            </div>
            <div className="text-caption text-text-tertiary font-mono truncate">
              {selected.file.key}
            </div>
          </div>
        )}
        {selected?.note && (
          <div className="text-body-sm text-text-secondary bg-surface-neutral rounded-xl px-4 py-3 mb-3">
            {selected.note}
          </div>
        )}
        {selected?.file && (
          <FileEditor
            key={selected.file.key}
            env={env}
            slug={slug}
            fileKey={selected.file.key}
            onSaved={onRefresh}
          />
        )}
        {!selected && (
          <div className="text-body-sm text-text-tertiary py-8 text-center">
            Select a skill or subskill file
          </div>
        )}
      </div>
    </div>
  );
}

export default SkillsTab;
